import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Card, CardHeader, CardTitle, CardContent } from '@/common/ui/card'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { AxiosError } from 'axios'
import getOperatorFormSchema from './utils/getOperatorFormSchema'
import {
	CreateOperatorRequest,
	OperatorDataResponse,
	UpdateOperatorRequest,
} from '../../types'
import { CREATE_OPERATOR_ERROR_FIELD_MAPPERS } from './mappers'
import { omit } from 'lodash-es'
import { Switch } from '@/common/ui/switch'
// import { useEffect } from 'react'

export interface OperatorFormProps {
	handleSubmit: (
		value: CreateOperatorRequest | UpdateOperatorRequest,
	) => Promise<OperatorDataResponse> | void
	defaultValue?: OperatorDataResponse
}

export default function OperatorForm({
	defaultValue,
	handleSubmit,
}: OperatorFormProps) {
	const isEditing = !!defaultValue

	const form = useForm<CreateOperatorRequest | UpdateOperatorRequest>({
		resolver: zodResolver(getOperatorFormSchema(isEditing)),
		defaultValues: {
			username: '',
			password: undefined,
			phone: '',
			userGroupId: '',
			isActive: undefined,
			...(defaultValue
				? (omit(defaultValue, ['password']) as
						| CreateOperatorRequest
						| UpdateOperatorRequest)
				: {}),
		},
	})

	const onSubmit = async (
		values: CreateOperatorRequest | UpdateOperatorRequest,
	) => {
		try {
			await handleSubmit(values)

			if (isEditing) {
				toast.success('แก้ไข operator สำเร็จ')
			} else {
				toast.success('สร้าง operator สำเร็จ')
				form.reset()
			}
		} catch (e) {
			if (e instanceof AxiosError) {
				const errorData = e.response?.data

				const errorField = CREATE_OPERATOR_ERROR_FIELD_MAPPERS[errorData.code]

				if (errorField) {
					form.setError(errorField, {
						type: 'validate',
						message: errorData.message,
					})

					return
				} else {
					showErrorToast(e)
				}
			} else {
				showErrorToast(e as Error)
			}
		}
	}

	return (
		<Card className="mx-auto w-full">
			<CardHeader>
				<CardTitle className="text-left text-2xl font-bold">
					สร้าง Operator
				</CardTitle>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
						<FormField
							control={form.control}
							name="username"
							render={({ field }) => (
								<FormItem>
									<FormLabel htmlFor="username">ชื่อผู้ใช้งาน</FormLabel>
									<FormControl>
										<Input placeholder="กรอกชื่อผู้ใช้งาน" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{!isEditing && (
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel htmlFor="password">รหัสผ่าน</FormLabel>
										<FormControl>
											<Input
												type="password"
												placeholder="กรอกชื่อผู้ใช้งาน"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{isEditing && (
							<FormField
								control={form.control}
								name="isActive"
								render={({ field }) => (
									<FormItem>
										<FormLabel htmlFor="isActive">เปิดใช้งาน</FormLabel>
										<br />
										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						<FormField
							control={form.control}
							name="phone"
							render={({ field }) => (
								<FormItem>
									<FormLabel htmlFor="phone">เบอร์โทรศัพท์</FormLabel>
									<FormControl>
										<Input placeholder="กรอกเบอร์โทรศัพท์" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="userGroupId"
							render={({ field }) => (
								<FormItem>
									<FormLabel htmlFor="userGroupId">User Group ID</FormLabel>
									<FormControl>
										<Input placeholder="กรอก User Group ID" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button disabled={form.formState.isSubmitting} type="submit">
							Submit
						</Button>
					</form>
				</Form>
			</CardContent>
		</Card>
	)
}
